import React, { forwardRef } from "react";
import Link, { LinkProps } from "next/link";
import { IconType } from "react-icons";

type ButtonVariant = "primary" | "secondary" | "tertiary";

type BaseButtonProps = {
  variant: ButtonVariant;
  icon?: IconType;
  children: React.ReactNode;
  className?: string;
  openInNewTab?: boolean;
};

type ButtonAsButtonProps = BaseButtonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: "button";
  };

type ButtonAsAnchorProps = BaseButtonProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    as: "a";
    href: string;
  };

type ButtonAsLinkProps = BaseButtonProps &
  Omit<LinkProps, "href"> & {
    as: typeof Link;
    href: string;
  };

type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps | ButtonAsLinkProps;

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant,
      icon: Icon,
      as: Component = "button",
      className = "",
      openInNewTab,
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      "relative inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

    const variantClasses = {
      primary:
        "bg-primary text-primary-foreground border border-primary hover:bg-primary/90 hover:border-primary/90 focus:ring-primary/20 shadow-sm hover:shadow-md",
      secondary:
        "bg-secondary text-secondary-foreground border border-border hover:bg-accent hover:text-accent-foreground hover:border-border/80 focus:ring-ring/20 shadow-sm hover:shadow-md",
      tertiary:
        "bg-background text-foreground border border-border hover:bg-accent hover:text-accent-foreground hover:border-border/80 focus:ring-ring/20 shadow-sm hover:shadow-md",
    };

    const gradientClasses = "";

    const classes = `${baseClasses} ${variantClasses[variant]} ${gradientClasses} ${className}`;

    const commonProps = {
      className: classes,
      ...(openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {}),
    };

    const content = (
      <span className="relative z-10 flex items-center justify-center">
        {children}
        {Icon && <Icon className="ml-2 h-4 w-4" />}
      </span>
    );

    if (Component === Link) {
      return (
        <Component {...commonProps} {...(props as ButtonAsLinkProps)}>
          {content}
        </Component>
      );
    } else if (Component === "a") {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...commonProps}
          {...(props as ButtonAsAnchorProps)}
        >
          {content}
        </a>
      );
    }
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        {...commonProps}
        {...(props as ButtonAsButtonProps)}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = "Button";
